import React, { useRef, useState } from "react"
import PropTypes from "prop-types"
import Column from "../../../../../components/atoms/table/Column.atom"
import clientFilterTemplate from "../../../../../components/compounds/registeredTasksTable/ClientFilterTemplate.compound"
import teamMemberFilterTemplate from "../../../../../components/compounds/registeredTasksTable/TeamMemberFilterTemplate.compound"
import InputTextForm from "../../../../../components/molecules/InputTextForm.molecule"
import DataTable from "../../../../../components/atoms/table/DataTable.atom"
import ClientProjectChip from "../../../../../components/molecules/ClientProjectChip.molecule"
import FollowersChipList from "../../../components/FollowersChipList.compound"
import {
  clsx,
  displayValueOrPlaceholder,
} from "../../../../../utils/strings.utils"
import IconWithTooltip from "../../../../../components/molecules/IconWithTooltip.molecule"
import useisHoldingModifierDown from "../../../../../hooks/useIsHoldingModifierDown"
import { Skeleton } from "../../../../../components/atoms/misc/Skeleton.atom"
import { FilterService } from "primereact/api"
import { unfinishPendingTaskById } from "../../../../../services/pendingTask.service"
import UnifinishHistoricalTask from "../../../components/UnifinishHistoricalTask.compound"
import { getDisplayISODate } from "../../../../../utils/date.utils"
import { formatDataTableFilterProjects } from "../../../../../utils/project.utils"
import { findClientWithProject } from "../../../../../utils/pendingTasks.utils"

FilterService.register("custom_followerIds", (value, filter) => {
  if (!filter || filter?.length === 0) return true
  if (!Array.isArray(value)) return false
  return true // Its already filtered  by backend
})

const cellLoadTemplate = () => (
  <div
    className={`flex align-items-center`}
    style={{
      flexGrow: "1",
      overflow: "hidden",
      width: "100%",
    }}
  >
    <Skeleton className="column-skeleton" height="1rem" />
  </div>
)

const HistoricalTasksDataTable = React.forwardRef(
  (
    {
      historicalTasks,
      sortConfig = {},
      setSortConfig,
      setFilterConfig,
      lazyLoading,
      projects,
      teamMembers,
      virtualScrollerOptions,
      triggerRefresh,
      inlineLoading,
      setInlineLoading,
      height,
      emptyCustomMessage,
      lastFinishChangeTaskID,
      setLastFinishChangeTaskID,
      taskIsBeingHidden,
      setTaskIsBeingHidden,
      loadingProjects,
      loadingState,
    },
    ref
  ) => {
    // Individual row statess
    const [editingId, setEditingId] = useState(null)
    const [hoveredId, setHoveredId] = useState(null)

    // Filter refs
    const clientFilterOptionsRef = useRef(null)
    const teamMemberFilterOptionsRef = useRef(null)

    const isHoldingModifierDown = useisHoldingModifierDown()
    return (
      <DataTable
        ref={ref}
        className={clsx(
          "historical-pending-tasks w-full p-datatable-custom-hover",
          historicalTasks.length === 0 && "disabled-filters",
          loadingState && "disabled-filters"
        )}
        tableClassName="relative w-full"
        value={historicalTasks}
        noSameWidthColumns
        noFixedColumns
        withOutHeader
        scrollable
        style={{ height: "100%" }}
        tableStyle={{
          tableLayout: "fixed",
          height: height,
          minHeight: height,
          maxHeight: height,
        }}
        sortField={sortConfig.sortField}
        sortOrder={sortConfig.sortOrder}
        onSort={(e) => {
          setSortConfig(e)
        }}
        removableSort
        virtualScrollerOptions={virtualScrollerOptions}
        onValueChange={() => {
          ref?.current?.resetScroll()
        }}
        globalFilterFields={["followerIds"]}
        onRowMouseEnter={(e) => {
          // Checar bug con e.data.id XDDD
          setHoveredId(historicalTasks[e?.index]?.id)
        }}
        onRowMouseLeave={() => {
          setHoveredId(null)
        }}
        emptyCustomMessage={emptyCustomMessage}
      >
        {ClientProjectColumn({
          setFilterConfig,
          projects,
          historicalTasks,
          clientFilterOptionsRef,
          isHoldingModifierDown,
          loadingState,
        })}
        <Column
          field="createdAt"
          header="Creación"
          body={(rowData, props) => {
            rowData = historicalTasks[props.rowIndex]
            const val = getDisplayISODate(rowData.createdAt)
            if (!rowData.createdAt) return cellLoadTemplate()
            return val
          }}
          sortable
          sortField="createdAt"
          style={{ minWidth: "114px", maxWidth: "114px" }}
        />
        <Column
          field="finishedAt"
          header="Finalización"
          body={(rowData, props) => {
            rowData = historicalTasks[props.rowIndex]
            const val = getDisplayISODate(rowData.finishedAt)
            if (!rowData.finishedAt) return cellLoadTemplate()
            return val
          }}
          sortable
          style={{ minWidth: "114px", maxWidth: "114px" }}
        />
        <Column
          field="name"
          header="Tarea"
          headerStyle={{ minWidth: "344px" }}
          style={{ minWidth: "344px", width: "344px" }}
          body={(rowData, props) => {
            rowData = historicalTasks[props.rowIndex]
            if (!rowData.name) return cellLoadTemplate()
            return (
              <div className="w-full flex align-items-center gap-2">
                <span
                  className="overflow-hidden white-space-nowrap text-overflow-ellipsis w-full"
                  title={rowData?.name}
                >
                  {rowData?.name}
                </span>
                {rowData?.comment ?
                  <IconWithTooltip
                    tooltipValue={rowData?.comment}
                    iconClassName="pi pi-comment"
                  />
                : null}
              </div>
            )
          }}
          filter
          filterMatchMode="contains"
          filterField="name"
          showFilterMatchModes={false}
          showFilterMenuOptions={false}
          filterElement={(options) => {
            return (
              <InputTextForm
                value={options.value || ""}
                onChange={(e) => {
                  const value = e.target.value
                  options.filterCallback(value)
                }}
                onBlur={() => {
                  options.filterCallback(options.value?.trim())
                }}
                autoTrim={false}
              />
            )
          }}
          onFilterApplyClick={(e) => {
            setFilterConfig((prev) => ({
              ...prev,
              taskName: e.constraints.constraints[0].value,
            }))
          }}
          onFilterClear={(e) => {
            setFilterConfig((prev) => ({
              ...prev,
              taskName: null,
            }))
          }}
        />
        {FollowerIdsColumn({
          teamMembers,
          teamMemberFilterOptionsRef,
          setFilterConfig,
          isHoldingModifierDown,
          historicalTasks,
          loadingState,
        })}
        <Column
          field="limitDate"
          header="Fecha límite"
          body={(rowData, props) => {
            rowData = historicalTasks[props.rowIndex]
            const val = getDisplayISODate(rowData.limitDate)
            if (!Object.keys(rowData).includes("limitDate"))
              return cellLoadTemplate()
            return val
          }}
          sortable
          style={{ minWidth: "134px", maxWidth: "134px" }}
        />
        <Column
          field="estimatedTime"
          header="Estimación"
          body={(rowData, props) => {
            rowData = historicalTasks[props.rowIndex]
            if (!Object.keys(rowData).includes("estimatedTime"))
              return cellLoadTemplate()
            return displayValueOrPlaceholder(rowData.estimatedTime)
          }}
          style={{ minWidth: "114px", maxWidth: "114px" }}
        />
        {UnifinishPendingTaskColumn({
          hoveredId,
          inlineLoading,
          setInlineLoading,
          editingId,
          setEditingId,
          lazyLoading,
          triggerRefresh,
          historicalTasks,
          lastFinishChangeTaskID,
          setLastFinishChangeTaskID,
          taskIsBeingHidden,
          setTaskIsBeingHidden,
        })}
      </DataTable>
    )
  }
)

export default HistoricalTasksDataTable

const UnifinishPendingTaskColumn = ({
  hoveredId,
  inlineLoading,
  setInlineLoading,
  editingId,
  setEditingId,
  lazyLoading,
  triggerRefresh,
  historicalTasks,
  lastFinishChangeTaskID,
  setLastFinishChangeTaskID,
  taskIsBeingHidden,
  setTaskIsBeingHidden,
}) => {
  return (
    <Column
      style={{ maxWidth: "50px", minWidth: "50px" }}
      body={(rowData, props) => {
        rowData = historicalTasks[props.rowIndex]
        if (!Object.keys(rowData).includes("id")) return cellLoadTemplate()
        // if (!hoveredId || !rowData.id) return null
        const id = `flag-unfinish-${rowData.id}`
        return (
          <>
            <UnifinishHistoricalTask
              rowData={rowData}
              loading={Boolean(inlineLoading && editingId === rowData.id)}
              onClick={async () => {
                setTaskIsBeingHidden(true)
                setLastFinishChangeTaskID(rowData.id)
                await unfinishPendingTaskById(rowData.id)
                setTimeout(() => {
                  triggerRefresh()
                }, 300)
              }}
              hidden={hoveredId !== rowData.id}
            />
            <div
              className={`${lastFinishChangeTaskID && lastFinishChangeTaskID === rowData.id && taskIsBeingHidden ? "historical-task-wrapper--fade-out " : ""}`}
            />
          </>
        )
      }}
    />
  )
}

const ClientProjectColumn = ({
  setFilterConfig,
  projects,
  historicalTasks,
  clientFilterOptionsRef,
  isHoldingModifierDown,
  loadingState,
}) => {
  return (
    <Column
      field="projectName"
      header="Proyecto"
      body={(rowData, props) => {
        rowData = historicalTasks[props.rowIndex]
        if (!rowData.projectName) return cellLoadTemplate()
        const handleClientCick = () => {
          if (!isHoldingModifierDown) return
          setFilterConfig((prev) => {
            // If the client is already selected, remove ALL associated projects
            const clients = formatDataTableFilterProjects(projects).filter(
              (project) => {
                const projectCodes = historicalTasks.map(
                  (task) => task.projectCode
                )
                return project.items.some((item) =>
                  projectCodes.includes(item.code)
                )
              }
            )
            const clientWithProject = findClientWithProject(
              clients,
              rowData.projectCode
            )
            let value = null
            if (clientWithProject) {
              value = clientWithProject.items.map((project) => project.name)
            }
            const isClientPrevSelected =
              prev.clientCode &&
              prev.clientCode.some((code) => code === clientWithProject.code)
            if (isClientPrevSelected) {
              value = null
            }
            setTimeout(() => {
              clientFilterOptionsRef.current.filterApplyCallback(value)
            }, 0)
            return {
              ...prev,
              clientCode: value ? [clientWithProject.code] : null,
            }
          })
        }

        const handleProjectClick = () => {
          if (!isHoldingModifierDown) return
          setFilterConfig((prev) => {
            // If the project is already selected, remove it
            const value =
              prev.projectName?.[0] === rowData.projectName ?
                null
              : [rowData.projectName]
            setTimeout(() => {
              clientFilterOptionsRef.current.filterApplyCallback(value)
            }, 0)
            return {
              ...prev,
              projectName: value,
            }
          })
        }
        return (
          <div className="flex gap-2 align-items-center project-chip-td">
            <ClientProjectChip
              clientCode={rowData.clientCode}
              projectCode={rowData.projectCode}
              className={`${isHoldingModifierDown ? "ctrl-selectable-badge" : ""}`}
              onClientClick={handleClientCick}
              onProjectClick={handleProjectClick}
            />
          </div>
        )
      }}
      style={{ minWidth: "114px", maxWidth: "114px" }}
      frozen
      filter
      filterMatchMode="in"
      filterField="projectName"
      showFilterMatchModes={false}
      showFilterMenuOptions={false}
      filterElement={(options) =>
        clientFilterTemplate({
          options,
          projects: formatDataTableFilterProjects(projects),
          clientFilterOptionsRef,
          loading: loadingState,
        })
      }
      onFilterApplyClick={(e) => {
        const filterValue = e.constraints.constraints[0].value
        setFilterConfig((prev) => ({
          ...prev,
          projectName: [...new Set(filterValue)],
        }))
      }}
      onFilterClear={(e) => {
        setFilterConfig((prev) => ({
          ...prev,
          projectName: null,
        }))
      }}
    />
  )
}
const FollowerIdsColumn = ({
  teamMembers,
  teamMemberFilterOptionsRef,
  setFilterConfig,
  isHoldingModifierDown,
  historicalTasks,
  loadingState,
}) => {
  return (
    <Column
      field="followerIds"
      header="Seguidores"
      body={(rowData, props) => {
        rowData = historicalTasks[props.rowIndex]
        const selectedFollowers = rowData.followerIds
          .map((value) => teamMembers.find((option) => option.id === value))
          .filter((follower) => follower !== undefined)
        if (rowData.followerIds.length && rowData.followerIds[0] === undefined)
          return cellLoadTemplate()
        if (selectedFollowers.length === 0) return "-"
        return (
          <FollowersChipList
            key={isHoldingModifierDown}
            selectedFollowers={selectedFollowers}
            onChange={(e) => {
              teamMemberFilterOptionsRef.current.filterApplyCallback([e.value])
              setFilterConfig((prev) => {
                const prevFollowers = prev.followerIds || []
                const value =
                  prevFollowers && prevFollowers.includes(e.value) ?
                    prevFollowers.filter((id) => id !== e.value)
                  : prevFollowers.concat(e.value)
                teamMemberFilterOptionsRef.current.filterApplyCallback(value)
                return {
                  ...prev,
                  followerIds: value,
                }
              })
            }}
            removableFollowers={isHoldingModifierDown}
            hoveredFollowerTemplate={(follower) => (
              <span>{follower.nameTag}</span>
            )}
            maxChips={5}
          />
        )
      }}
      style={{ minWidth: "134px", maxWidth: "134px" }}
      headerStyle={{ minWidth: "134px", maxWidth: "134px" }}
      filter
      filterMatchMode="custom"
      showFilterMatchModes={false}
      showFilterMenuOptions={false}
      filterField="followerIds"
      filterElement={(options) => {
        return teamMemberFilterTemplate({
          options,
          teamMembers,
          teamMemberFilterOptionsRef,
          noOptionLabel: "Sin seguidores",
          loading: loadingState,
        })
      }}
      onFilterApplyClick={(e) => {
        const filterValue = e.constraints.constraints[0].value
        teamMemberFilterOptionsRef.current.filterApplyCallback(filterValue)
        setFilterConfig((prev) => ({
          ...prev,
          followerIds: filterValue,
        }))
      }}
      onFilterClear={(e) => {
        setFilterConfig((prev) => ({
          ...prev,
          followerIds: null,
        }))
      }}
    />
  )
}

HistoricalTasksDataTable.propTypes = {
  historicalTasks: PropTypes.array.isRequired,
  sortConfig: PropTypes.object,
  setSortConfig: PropTypes.func,
  setFilterConfig: PropTypes.func,
  lazyLoading: PropTypes.bool,
  projects: PropTypes.array,
  teamMembers: PropTypes.array,
  virtualScrollerOptions: PropTypes.object,
  triggerRefresh: PropTypes.func,
  inlineLoading: PropTypes.bool,
  setInlineLoading: PropTypes.func,
  height: PropTypes.number,
  emptyCustomMessage: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
}
